// V4.12 Phase C — Premium Heatmap chip bar.
//
// Context-dependent chips (Founder spec): the map shows as little UI as
// possible. One vertical switch (Auto/Immo), one row of mode chips
// (🔥 Deals · 📊 Markt · …) and a subtle second row of filter chips that
// open small anchored popovers. All filter values are the CANONICAL
// explore/heatmap params — the same vocabulary the AI search uses.
import React, { useState } from "react";
import { Car, ChevronDown, Home, Search, X } from "lucide-react";
import { enumLabel, featureLabel } from "../../utils/enumLabels";

export const csvList = (s) => (s ? String(s).split(",").filter(Boolean) : []);
const csvToggle = (s, v) => {
  const cur = csvList(s);
  return (cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]).join(",");
};
const summarize = (labels, max = 2) =>
  labels.length <= max
    ? labels.join(", ")
    : `${labels.slice(0, max).join(", ")} +${labels.length - max}`;
const fmtK = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  return n >= 1000 ? `${Math.round(n / 1000)}k` : String(n);
};

export const MODES = {
  // Master-Sprint 2026-06 — "alle"/"ueberteuert" are no longer MODES:
  // "Markt" now shows the FULL market and gets a market-status
  // sub-filter (all/below/near/above/no signal). Legacy URLs are
  // normalized in HeatmapPage.
  auto: [
    { id: "deals", icon: "🔥" },
    { id: "markt", icon: "📊" },
  ],
  immo: [
    { id: "deals", icon: "🔥" },
    { id: "markt", icon: "📊" },
    { id: "preis_m2", icon: "💶" },
    { id: "rendite", icon: "📈" },
  ],
};

// Market-status sub-filter (only meaningful in "markt" mode).
export const STATUS_OPTIONS = [
  { value: "", key: "all" },
  { value: "below_market_all", key: "below" },
  { value: "near_market", key: "near" },
  { value: "above_market_all", key: "above" },
  { value: "no_signal", key: "nosig" },
];

export function StatusSegment({ status, onStatus, t, compact }) {
  return (
    <div
      data-testid="heatmap-status-segment"
      className={`flex items-center gap-1.5 ${compact ? "" : "flex-wrap"}`}
    >
      {STATUS_OPTIONS.map((o) => (
        <button
          key={o.key}
          type="button"
          data-testid={`heatmap-status-${o.key}`}
          aria-pressed={(status || "") === o.value}
          onClick={() => onStatus(o.value)}
          className={`shrink-0 rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
            (status || "") === o.value
              ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
              : "border-zinc-800 bg-zinc-950 text-zinc-500 hover:text-zinc-300"
          }`}
        >
          {t(`status.${o.key}`)}
        </button>
      ))}
    </div>
  );
}

function Popover({ onClose, children, wide }) {
  return (
    <>
      <div
        className="fixed inset-0 z-[1090]"
        onClick={onClose}
        data-testid="chip-popover-overlay"
      />
      <div
        data-testid="chip-popover"
        className={`absolute left-0 top-full z-[1100] mt-2 ${
          wide ? "w-[320px]" : "w-64"
        } rounded-xl border border-zinc-700/80 bg-zinc-900/95 p-3 shadow-2xl shadow-black/60 backdrop-blur animate-in fade-in slide-in-from-top-1 duration-150`}
      >
        {children}
      </div>
    </>
  );
}

function Chip({ id, label, value, open, onOpen, onClear, disabled, children }) {
  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        data-testid={`heatmap-chip-${id}`}
        onClick={onOpen}
        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
          value
            ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
            : open
              ? "border-zinc-600 bg-zinc-800 text-zinc-100"
              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
        } ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
      >
        {label}
        {value && <span className="max-w-[140px] truncate font-semibold">{value}</span>}
        {value ? (
          <span
            role="button"
            tabIndex={0}
            data-testid={`heatmap-chip-${id}-clear`}
            onClick={(e) => {
              e.stopPropagation();
              onClear();
            }}
            className="rounded-full p-0.5 hover:bg-amber-500/20"
          >
            <X className="h-3 w-3" />
          </span>
        ) : (
          <ChevronDown className="h-3 w-3 opacity-60" />
        )}
      </button>
      {open && children}
    </div>
  );
}

export function MultiPicker({ options, selectedCsv, onToggle, searchable, searchPlaceholder }) {
  const [q, setQ] = useState("");
  const sel = csvList(selectedCsv);
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : options;
  return (
    <div className="space-y-2">
      {searchable && (
        <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-1.5">
          <Search className="h-3.5 w-3.5 text-zinc-500" />
          <input
            autoFocus
            data-testid="chip-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={searchPlaceholder}
            className="w-full bg-transparent text-xs text-zinc-100 outline-none placeholder:text-zinc-600"
          />
        </div>
      )}
      <div className="flex max-h-56 flex-wrap gap-1.5 overflow-y-auto">
        {filtered.map((o) => (
          <button
            key={o.value}
            type="button"
            data-testid={`chip-opt-${o.value}`}
            onClick={() => onToggle(o.value)}
            className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${
              sel.includes(o.value)
                ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {o.label}
          </button>
        ))}
        {filtered.length === 0 && (
          <span className="text-[11px] text-zinc-600">—</span>
        )}
      </div>
    </div>
  );
}

export function RegionEditor({ filters, onPatch, onClose, t }) {
  const [val, setVal] = useState(filters.region || "");
  const [sugs, setSugs] = useState([]);
  const apply = (v) => {
    onPatch({ region: (v ?? val).trim() });
    onClose();
  };
  const onType = async (v) => {
    setVal(v);
    if (v.trim().length < 2) { setSugs([]); return; }
    try {
      const { searchPlaces } = await import("../../api/taxonomyApi");
      setSugs((await searchPlaces(v.trim(), filters.country)) .slice(0, 8));
    } catch { setSugs([]); }
  };
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-1.5">
        <Search className="h-3.5 w-3.5 text-zinc-500" />
        <input
          autoFocus
          data-testid="chip-region-input"
          value={val}
          onChange={(e) => onType(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && apply()}
          placeholder={t("chips.regionPlaceholder")}
          className="w-full bg-transparent text-xs text-zinc-100 outline-none placeholder:text-zinc-600"
        />
      </div>
      {sugs.length > 0 && (
        <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
          {sugs.map((s) => (
            <button
              key={s.display}
              type="button"
              data-testid={`chip-region-sug-${s.display}`}
              onClick={() => apply(s.display)}
              className="rounded-full border border-zinc-800 bg-zinc-950 px-2.5 py-1 text-[11px] font-medium text-zinc-400 transition-colors hover:text-zinc-200"
            >
              {s.display}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        data-testid="chip-region-apply"
        onClick={() => apply()}
        className="w-full rounded-lg bg-amber-500 py-1.5 text-xs font-semibold text-zinc-950 transition-colors hover:bg-amber-400"
      >
        {t("chips.regionApply")}
      </button>
    </div>
  );
}

export function RangeEditor({ loKey, hiKey, filters, onPatch, onClose, t, unit }) {
  const [lo, setLo] = useState(filters[loKey] || "");
  const [hi, setHi] = useState(hiKey ? filters[hiKey] || "" : "");
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <input
          type="number"
          min="0"
          autoFocus
          data-testid={`chip-range-${loKey}`}
          value={lo}
          onChange={(e) => setLo(e.target.value)}
          placeholder={t("chips.from")}
          className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-amber-500/50"
        />
        {hiKey && (
          <>
            <span className="text-zinc-600">–</span>
            <input
              type="number"
              min="0"
              data-testid={`chip-range-${hiKey}`}
              value={hi}
              onChange={(e) => setHi(e.target.value)}
              placeholder={t("chips.to")}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-1.5 text-xs text-zinc-100 outline-none focus:border-amber-500/50"
            />
          </>
        )}
        {unit && <span className="text-[11px] text-zinc-500">{unit}</span>}
      </div>
      <button
        type="button"
        data-testid="chip-range-apply"
        onClick={() => {
          const patch = { [loKey]: lo };
          if (hiKey) patch[hiKey] = hi;
          onPatch(patch);
          onClose();
        }}
        className="w-full rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-zinc-950 hover:bg-amber-400"
      >
        {t("chips.apply")}
      </button>
    </div>
  );
}

export const HeatmapChipBar = ({
  vertical,
  mode,
  filters,
  onVertical,
  onMode,
  onPatch,
  autoOptions,
  immoOptions,
  modelOptions,
  hideModeRow,
  t,
}) => {
  const [openChip, setOpenChip] = useState(null);
  const open = (id) => setOpenChip((c) => (c === id ? null : id));
  const close = () => setOpenChip(null);
  const toggleCsv = (key, val) => onPatch({ [key]: csvToggle(filters[key], val) });

  const brandLabel = (slug) =>
    (autoOptions?.brands || []).find((b) => b.value === slug)?.label || slug;

  const chip = (id, label, value, body, opts = {}) => (
    <Chip
      key={id}
      id={id}
      label={label}
      value={value}
      open={openChip === id}
      onOpen={() => open(id)}
      onClear={() => onPatch(opts.clearPatch || { [opts.clearKey || id]: "" })}
      disabled={opts.disabled}
    >
      <Popover onClose={close} wide={opts.wide}>{body}</Popover>
    </Chip>
  );

  const regionChip = chip(
    "region", t("chips.region"),
    filters.region || null,
    <RegionEditor filters={filters} onPatch={onPatch} onClose={close} t={t} />,
    { wide: true },
  );

  // Master-Sprint P1 — price-reduction filter (source-documented drops
  // only; same param for both verticals).
  const priceReducedChip = chip(
    "price_reduced", t("chips.priceReduced"),
    filters.price_reduced ? t("chips.priceReducedOn") : null,
    <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-300">
      <input
        type="checkbox"
        data-testid="chip-price-reduced-toggle"
        checked={filters.price_reduced === "1"}
        onChange={(e) => {
          onPatch({ price_reduced: e.target.checked ? "1" : "" });
          close();
        }}
        className="h-3.5 w-3.5 accent-amber-500"
      />
      {t("chips.priceReducedOnly")}
    </label>,
    { clearKey: "price_reduced" },
  );

  const autoChips = [
    chip(
      "makes", t("chips.make"),
      csvList(filters.makes).length
        ? summarize(csvList(filters.makes).map(brandLabel))
        : null,
      <MultiPicker
        options={autoOptions?.brands || []}
        selectedCsv={filters.makes}
        onToggle={(v) => toggleCsv("makes", v)}
        searchable
        searchPlaceholder={t("chips.searchMake")}
      />,
      { wide: true },
    ),
    chip(
      "models", t("chips.model"),
      csvList(filters.models).length ? summarize(csvList(filters.models)) : null,
      csvList(filters.makes).length ? (
        <MultiPicker
          options={modelOptions || []}
          selectedCsv={filters.models}
          onToggle={(v) => toggleCsv("models", v)}
          searchable
          searchPlaceholder={t("chips.model")}
        />
      ) : (
        <div className="text-[11px] text-zinc-500">{t("chips.pickMakeFirst")}</div>
      ),
      { wide: true },
    ),
    chip(
      "year", t("chips.year"),
      filters.year_min || filters.year_max
        ? `${filters.year_min || "…"}–${filters.year_max || "…"}`
        : null,
      <RangeEditor loKey="year_min" hiKey="year_max" filters={filters} onPatch={onPatch} onClose={close} t={t} />,
      { clearPatch: { year_min: "", year_max: "" } },
    ),
    chip(
      "km", t("chips.km"),
      filters.km_max ? `≤ ${fmtK(filters.km_max)} km` : null,
      <RangeEditor loKey="km_max" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="km" />,
      { clearKey: "km_max" },
    ),
    chip(
      "price", t("chips.price"),
      filters.price_min || filters.price_max
        ? `${filters.price_min ? fmtK(filters.price_min) : "…"}–${filters.price_max ? fmtK(filters.price_max) : "…"} €`
        : null,
      <RangeEditor loKey="price_min" hiKey="price_max" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="€" />,
      { clearPatch: { price_min: "", price_max: "" } },
    ),
    chip(
      "fuels", t("chips.fuel"),
      csvList(filters.fuels).length
        ? summarize(csvList(filters.fuels).map((v) => enumLabel("fuel", v)))
        : null,
      <MultiPicker
        options={(autoOptions?.fuels || []).map((o) => ({ value: o.value, label: enumLabel("fuel", o.value) }))}
        selectedCsv={filters.fuels}
        onToggle={(v) => toggleCsv("fuels", v)}
      />,
    ),
    chip(
      "gearboxes", t("chips.gearbox"),
      csvList(filters.gearboxes).length
        ? summarize(csvList(filters.gearboxes).map((v) => enumLabel("gearbox", v)))
        : null,
      <MultiPicker
        options={(autoOptions?.gearboxes || []).map((o) => ({ value: o.value, label: enumLabel("gearbox", o.value) }))}
        selectedCsv={filters.gearboxes}
        onToggle={(v) => toggleCsv("gearboxes", v)}
      />,
    ),
    chip(
      "body_types", t("chips.body"),
      csvList(filters.body_types).length
        ? summarize(csvList(filters.body_types).map((v) => enumLabel("body_type", v)))
        : null,
      <MultiPicker
        options={(autoOptions?.body_types || []).map((o) => ({ value: o.value, label: enumLabel("body_type", o.value) }))}
        selectedCsv={filters.body_types}
        onToggle={(v) => toggleCsv("body_types", v)}
      />,
      { wide: true },
    ),
    chip(
      "features", t("chips.features"),
      csvList(filters.features).length
        ? summarize(csvList(filters.features).map(featureLabel))
        : null,
      <MultiPicker
        options={(autoOptions?.features || []).map((o) => ({ value: o.value, label: featureLabel(o.value) }))}
        selectedCsv={filters.features}
        onToggle={(v) => toggleCsv("features", v)}
      />,
      { wide: true },
    ),
    chip(
      "seller_types", t("chips.seller"),
      csvList(filters.seller_types).length
        ? summarize(csvList(filters.seller_types).map((v) => enumLabel("seller_type", v)))
        : null,
      <MultiPicker
        options={["private", "dealer"].map((v) => ({ value: v, label: enumLabel("seller_type", v) }))}
        selectedCsv={filters.seller_types}
        onToggle={(v) => toggleCsv("seller_types", v)}
      />,
    ),
    chip(
      "damage", t("chips.damage"),
      filters.damage ? t("chips.damageOn") : null,
      <label className="flex cursor-pointer items-center gap-2 text-xs text-zinc-300">
        <input
          type="checkbox"
          data-testid="chip-damage-toggle"
          checked={filters.damage === "1"}
          onChange={(e) => {
            onPatch({ damage: e.target.checked ? "1" : "" });
            close();
          }}
          className="h-3.5 w-3.5 accent-amber-500"
        />
        {t("chips.includeDamaged")}
      </label>,
      { clearKey: "damage" },
    ),
  ];

  const immoChips = [
    <div key="tt" className="flex overflow-hidden rounded-full border border-zinc-800" data-testid="heatmap-chip-tt">
      {["buy", "rent"].map((tt) => (
        <button
          key={tt}
          type="button"
          data-testid={`explore-tt-${tt}`}
          onClick={() => onPatch({ tt })}
          className={`px-3 py-1.5 text-xs font-medium transition-colors ${
            filters.tt === tt
              ? "bg-amber-500 font-semibold text-zinc-950"
              : "bg-zinc-950 text-zinc-400 hover:text-zinc-200"
          }`}
        >
          {t(`chips.${tt}`)}
        </button>
      ))}
    </div>,
    chip(
      "types", t("chips.objectType"),
      csvList(filters.types).length
        ? summarize(csvList(filters.types).map((v) => enumLabel("object_type", v)))
        : null,
      <MultiPicker
        options={(immoOptions?.object_types || []).map((o) => ({ value: o.value, label: enumLabel("object_type", o.value) }))}
        selectedCsv={filters.types}
        onToggle={(v) => toggleCsv("types", v)}
      />,
      { wide: true },
    ),
    chip(
      "price", t("chips.price"),
      filters.price_min || filters.price_max
        ? `${filters.price_min ? fmtK(filters.price_min) : "…"}–${filters.price_max ? fmtK(filters.price_max) : "…"} €`
        : null,
      <RangeEditor loKey="price_min" hiKey="price_max" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="€" />,
      { clearPatch: { price_min: "", price_max: "" } },
    ),
    chip(
      "area", t("chips.area"),
      filters.area_min || filters.area_max
        ? `${filters.area_min || "…"}–${filters.area_max || "…"} m²`
        : null,
      <RangeEditor loKey="area_min" hiKey="area_max" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="m²" />,
      { clearPatch: { area_min: "", area_max: "" } },
    ),
    chip(
      "rooms", t("chips.rooms"),
      filters.rooms_min ? `${filters.rooms_min}+` : null,
      <RangeEditor loKey="rooms_min" filters={filters} onPatch={onPatch} onClose={close} t={t} />,
      { clearKey: "rooms_min" },
    ),
    chip(
      "yield", t("chips.yield"),
      filters.yield_min ? `${filters.yield_min} %+` : null,
      <RangeEditor loKey="yield_min" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="%" />,
      { clearKey: "yield_min" },
    ),
    chip(
      "discount", t("filtersSheet.discountMin"),
      filters.discount_min ? `${filters.discount_min} %+` : null,
      <RangeEditor loKey="discount_min" filters={filters} onPatch={onPatch} onClose={close} t={t} unit="%" />,
      { clearKey: "discount_min" },
    ),
    chip(
      "features", t("chips.features"),
      csvList(filters.features).length
        ? summarize(csvList(filters.features).map(featureLabel))
        : null,
      <MultiPicker
        options={(immoOptions?.features || []).map((o) => ({ value: o.value, label: featureLabel(o.value) }))}
        selectedCsv={filters.features}
        onToggle={(v) => toggleCsv("features", v)}
      />,
      { wide: true },
    ),
    chip(
      "seller_types", t("chips.seller"),
      csvList(filters.seller_types).length
        ? summarize(csvList(filters.seller_types).map((v) => enumLabel("seller_type", v)))
        : null,
      <MultiPicker
        options={["private", "broker", "developer"].map((v) => ({ value: v, label: enumLabel("seller_type", v) }))}
        selectedCsv={filters.seller_types}
        onToggle={(v) => toggleCsv("seller_types", v)}
      />,
    ),
  ];

  return (
    <div className="space-y-2.5">
      {/* Row 1 — vertical + mode chips (hidden inside the mobile
          filter sheet: modes stay on the map, only detail filters
          live in the sheet). */}
      {!hideModeRow && (
      <div className="flex flex-wrap items-center gap-2.5" data-testid="heatmap-mode-chips">
        <div className="flex overflow-hidden rounded-full border border-zinc-800 bg-zinc-950">
          {[
            { v: "auto", Icon: Car, label: t("chips.vertAuto") },
            { v: "immo", Icon: Home, label: t("chips.vertImmo") },
          ].map(({ v, Icon, label }) => (
            <button
              key={v}
              type="button"
              data-testid={`heatmap-vertical-${v}`}
              aria-pressed={vertical === v}
              onClick={() => onVertical(v)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold transition-colors ${
                vertical === v
                  ? "bg-amber-500 text-zinc-950"
                  : "text-zinc-400 hover:text-zinc-200"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
            </button>
          ))}
        </div>
        <div className="h-5 w-px bg-zinc-800" aria-hidden="true" />
        {MODES[vertical].map((m) => (
          <button
            key={m.id}
            type="button"
            data-testid={`heatmap-mode-${m.id}`}
            aria-pressed={mode === m.id}
            onClick={() => onMode(m.id)}
            className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-xs font-semibold transition-colors ${
              mode === m.id
                ? "border-amber-500/60 bg-amber-500/15 text-amber-300"
                : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
            }`}
          >
            <span aria-hidden="true">{m.icon}</span>
            {t(`chips.mode_${m.id}`)}
          </button>
        ))}
        {mode === "markt" && (
          <>
            <div className="h-5 w-px bg-zinc-800" aria-hidden="true" />
            <StatusSegment
              status={filters.status || ""}
              onStatus={(s) => onPatch({ status: s })}
              t={t}
            />
          </>
        )}
      </div>
      )}

      {/* Row 2 — context-dependent filter chips */}
      <div className="flex flex-wrap items-center gap-2" data-testid="heatmap-subchips">
        {vertical === "auto" ? autoChips : immoChips}
        {priceReducedChip}
        {regionChip}
      </div>
    </div>
  );
};
